import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import type { RoastExtraColumnKey, RoastProfileEdit, RoastProfileEditableKey, RoastProfileRow } from './types';
import { useLanguage } from '../../../features/language/useLanguage';
import { RoastProfileTableToolbar } from './RoastProfileTableToolbar';
import { RoastProfileCellInput } from './RoastProfileCellInput';
import { RoastProfileEditLedger } from './RoastProfileEditLedger';
import { buildEditKey, clampTemperatureInput } from './RoastProfileUtils';

interface RoastProfileTableProps {
  rows: RoastProfileRow[];
  readOnly: boolean;
  extraColumns: RoastExtraColumnKey[];
  onExtraColumnsChange: (value: RoastExtraColumnKey[]) => void;
  updateCell: (index: number, key: keyof Omit<RoastProfileRow, 'time' | 'seconds'>, value: string) => void;
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  minHeight?: number;
  onAddManualRow?: (timeStr: string) => void;
  onDeleteManualRow?: (index: number) => void;
  /** Cells that no longer match the imported profile. Omit to disable edit tracking. */
  edits?: RoastProfileEdit[];
  onResetCell?: (index: number, key: RoastProfileEditableKey) => void;
  onResetField?: (key: RoastProfileEditableKey) => void;
  onResetAll?: () => void;
}

export const RoastProfileTable: React.FC<RoastProfileTableProps> = React.memo(({
  rows,
  readOnly,
  extraColumns,
  onExtraColumnsChange,
  updateCell,
  tableContainerRef,
  minHeight,
  onAddManualRow,
  onDeleteManualRow,
  edits,
  onResetCell,
  onResetField,
  onResetAll,
}) => {
  const { t } = useLanguage();

  const editsByCell = useMemo(() => {
    const map = new Map<string, RoastProfileEdit>();
    for (const edit of edits ?? []) map.set(buildEditKey(edit.seconds, edit.key), edit);
    return map;
  }, [edits]);

  /** Edit-state props for one cell; empty when the caller isn't tracking edits. */
  const editProps = (row: RoastProfileRow, index: number, key: RoastProfileEditableKey) => {
    const edit = editsByCell.get(buildEditKey(row.seconds, key));
    if (!edit) return {};

    return {
      edited: true,
      revertLabel:
        edit.baseline === null
          ? t('roast.profileTable.edits.revertToEmpty')
          : t('roast.profileTable.edits.revertTo', { value: edit.baseline }),
      onRevert: onResetCell ? () => onResetCell(index, key) : undefined,
    };
  };

  // Check if a row is a "manual" row (not on a 30-second boundary)
  const isManualRow = (row: RoastProfileRow) => row.seconds % 30 !== 0;
  const hasAirTemp = extraColumns.includes('airTemp');
  const hasDrumTemp = extraColumns.includes('drumTemp');
  const hasEnvironmentTemp = extraColumns.includes('environmentTemp');
  const hasDrumRpm = extraColumns.includes('drumRpm');

  type RorCellMeta =
    | { kind: 'carrier'; span: number; ror: number | null }
    | { kind: 'covered' }
    | { kind: 'empty' };

  const rowMeta = useMemo(() => {
    const meta: RorCellMeta[] = new Array(rows.length);
    let nonManualIdx = -1;
    let coveredUntilExclusive = -1;

    for (let i = 0; i < rows.length; i++) {
      const isManual = rows[i].seconds % 30 !== 0;
      if (!isManual) nonManualIdx += 1;

      if (i < coveredUntilExclusive) {
        meta[i] = { kind: 'covered' };
        continue;
      }

      if (!isManual && nonManualIdx % 2 !== 0 && nonManualIdx > 0) {
        let span = 1;
        for (let j = i + 1; j < rows.length; j++) {
          span += 1;
          if (rows[j].seconds % 30 === 0) break;
        }
        meta[i] = { kind: 'carrier', span, ror: rows[i].ror ?? null };
        coveredUntilExclusive = i + span;
      } else {
        meta[i] = { kind: 'empty' };
      }
    }
    return meta;
  }, [rows]);

  return (
    <div className="border rounded-lg bg-white transition-all duration-300 min-w-0">
      <RoastProfileTableToolbar
        rows={rows}
        readOnly={readOnly}
        extraColumns={extraColumns}
        onExtraColumnsChange={onExtraColumnsChange}
        onAddManualRow={onAddManualRow}
      />

      {!readOnly && edits && edits.length > 0 && onResetField && onResetAll && (
        <RoastProfileEditLedger edits={edits} onResetField={onResetField} onResetAll={onResetAll} />
      )}

      <div
        ref={tableContainerRef}
        className="overflow-auto border-t"
        style={{
          maxHeight: '600px',
          ...(minHeight ? { minHeight } : {}),
        }}
      >
        <table className="w-full text-xs border-collapse sticky top-0">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="border p-1 text-center">{t('reports.shift.time')}</th>
              <th className="border p-1 text-center">{t('roast.profileTable.temp')}</th>
              <th className="border p-1 text-center">{t('roast.profileTable.ror')}</th>
              <th className="border p-1 text-center">{t('roast.profileTable.fire')}</th>
              <th className="border p-1 text-center">{t('roast.profileTable.airflow')}</th>
              {hasAirTemp && <th className="border p-1 text-center">{t('roast.profileTable.airTemp')}</th>}
              {hasDrumTemp && <th className="border p-1 text-center">{t('roast.profileTable.drumTemp')}</th>}
              {hasEnvironmentTemp && <th className="border p-1 text-center">{t('roast.profileTable.environmentTemp')}</th>}
              {hasDrumRpm && <th className="border p-1 text-center">{t('roast.profileTable.drumRpm')}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const manual = isManualRow(row);

              const cell = rowMeta[i];
              let rorCell: React.ReactNode = null;
              if (cell.kind === 'covered') {
                rorCell = null;
              } else if (cell.kind === 'carrier') {
                rorCell = (
                  <td className="border p-1 bg-gray-50" rowSpan={cell.span}>
                    <div className="text-center text-gray-700 font-medium" data-test-id={`profile-ror-${i}`}>
                      {cell.ror !== null ? cell.ror : ''}
                    </div>
                  </td>
                );
              } else {
                rorCell = <td className="border p-1 bg-gray-50"></td>;
              }

              return (
                <tr key={`${row.seconds}-${i}`} data-test-id={`profile-row-${i}`} className={manual ? 'bg-amber-50' : ''}>
                  <td
                    className={`border p-1 text-center font-medium relative group ${manual ? 'bg-amber-50 text-amber-800' : 'bg-gray-50'}`}
                    data-test-id={`profile-time-${i}`}
                  >
                    {row.time}
                    {manual && !readOnly && onDeleteManualRow && (
                      <button
                        type="button"
                        onClick={() => onDeleteManualRow(i)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all p-0.5"
                        title={t('roast.profileTable.removeRow')}
                        data-test-id={`profile-delete-row-${i}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </td>

                  <RoastProfileCellInput
                    dataTestId={`profile-bean-${i}`}
                    value={row.bean ?? ''}
                    manual={manual}
                    readOnly={readOnly}
                    onChange={(value) => updateCell(i, 'bean', clampTemperatureInput(value))}
                    {...editProps(row, i, 'bean')}
                  />

                  {rorCell}

                  <RoastProfileCellInput
                    dataTestId={`profile-fire-${i}`}
                    value={row.fire ?? ''}
                    manual={manual}
                    readOnly={readOnly}
                    onChange={(value) => updateCell(i, 'fire', value)}
                    {...editProps(row, i, 'fire')}
                  />

                  <RoastProfileCellInput
                    dataTestId={`profile-airflow-${i}`}
                    value={row.airflow ?? ''}
                    manual={manual}
                    readOnly={readOnly}
                    onChange={(value) => updateCell(i, 'airflow', value)}
                    {...editProps(row, i, 'airflow')}
                  />

                  {hasAirTemp && (
                    <RoastProfileCellInput
                      dataTestId={`profile-air-temp-${i}`}
                      value={row.airTemp ?? ''}
                      manual={manual}
                      readOnly={readOnly}
                      onChange={(value) => updateCell(i, 'airTemp', clampTemperatureInput(value))}
                      {...editProps(row, i, 'airTemp')}
                    />
                  )}

                  {hasDrumTemp && (
                    <RoastProfileCellInput
                      dataTestId={`profile-drum-temp-${i}`}
                      value={row.drumTemp ?? ''}
                      manual={manual}
                      readOnly={readOnly}
                      onChange={(value) => updateCell(i, 'drumTemp', clampTemperatureInput(value))}
                      {...editProps(row, i, 'drumTemp')}
                    />
                  )}

                  {hasEnvironmentTemp && (
                    <RoastProfileCellInput
                      dataTestId={`profile-environment-temp-${i}`}
                      value={row.environmentTemp ?? ''}
                      manual={manual}
                      readOnly={readOnly}
                      onChange={(value) => updateCell(i, 'environmentTemp', clampTemperatureInput(value))}
                      {...editProps(row, i, 'environmentTemp')}
                    />
                  )}

                  {hasDrumRpm && (
                    <RoastProfileCellInput
                      dataTestId={`profile-drum-rpm-${i}`}
                      value={row.drumRpm ?? ''}
                      manual={manual}
                      readOnly={readOnly}
                      numericOnly
                      onChange={(value) => updateCell(i, 'drumRpm', value)}
                      {...editProps(row, i, 'drumRpm')}
                    />
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});
